import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Loader2 } from "lucide-react";

// Modal genérico de vista previa (imagen o PDF) para documentos del Perfil de
// Viajero. StrictMode-safe: no revocamos el blob URL en el cleanup del effect
// (que corre 2 veces en dev y volaba la URL). Se revoca sólo al reemplazar la
// URL con una nueva o al desmontar el componente.
export function DocumentPreviewDialog({ open, onClose, url, filename, label, testIdPrefix = "doc-preview" }) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobType, setBlobType] = useState("");
  const urlRef = useRef(null); // guarda el object URL vigente para revocar

  // Tipo deducido por extensión como fallback si el server no devuelve content-type útil.
  const extType = useMemo(() => {
    const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) return `image/${ext === "jpg" ? "jpeg" : ext}`;
    if (ext === "pdf") return "application/pdf";
    return "";
  }, [filename]);

  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(url, { withCredentials: true, responseType: "blob" });
        if (cancelled) return;
        const headerType = (r.headers?.["content-type"] || "").split(";")[0].trim();
        const nativeType = r.data?.type || "";
        const finalType = headerType || nativeType || extType || "application/octet-stream";
        // Reconstruimos el blob con el tipo explícito para que el navegador
        // reconozca PDFs/imágenes al renderizar en <img> / <iframe>.
        const blob = new Blob([r.data], { type: finalType });
        const newUrl = URL.createObjectURL(blob);
        // Revoca la URL anterior (si había) antes de reemplazarla
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = newUrl;
        setBlobUrl(newUrl);
        setBlobType(finalType);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || "No se pudo abrir el documento");
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      // Sólo marcamos cancelado; NO revocamos aquí porque StrictMode ejecuta
      // este cleanup dos veces en dev y dejaba el iframe con URL muerta.
      cancelled = true;
    };
  }, [open, url, extType, onClose]);

  // Al cerrar el modal: revoca el URL vigente y limpia estado
  useEffect(() => {
    if (!open) {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setBlobUrl(null);
      setBlobType("");
    }
  }, [open]);

  // Al desmontar el componente definitivamente
  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, []);

  const downloadFromPreview = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "documento";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const isImage = blobType.startsWith("image/");
  const isPdf = blobType === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] p-0 overflow-hidden"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 pr-8">
            <FileText className="w-5 h-5 text-indigo-600 shrink-0" />
            <span className="truncate">{label || "Documento"}</span>
            {filename && (
              <span className="text-xs font-normal text-slate-500 truncate">· {filename}</span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div
          className="bg-slate-100 flex items-center justify-center overflow-auto"
          style={{ minHeight: "60vh", maxHeight: "70vh" }}
        >
          {loading && (
            <div className="flex items-center gap-2 text-slate-600 p-6" data-testid={`${testIdPrefix}-loading`}>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Cargando documento…</span>
            </div>
          )}
          {!loading && blobUrl && isImage && (
            <img
              src={blobUrl}
              alt={label || "Vista previa"}
              className="max-w-full max-h-[70vh] object-contain"
              data-testid={`${testIdPrefix}-image`}
            />
          )}
          {!loading && blobUrl && isPdf && (
            <object
              data={blobUrl}
              type="application/pdf"
              className="w-full bg-white"
              style={{ height: "70vh" }}
              data-testid={`${testIdPrefix}-pdf`}
            >
              <div className="text-center p-8 text-sm text-slate-600">
                <FileText className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p>Tu navegador no puede mostrar el PDF en línea.</p>
                <p className="text-xs text-slate-500 mt-1">Usa el botón <b>Descargar</b> para verlo.</p>
              </div>
            </object>
          )}
          {!loading && blobUrl && !isImage && !isPdf && (
            <div className="text-center p-8 text-sm text-slate-600" data-testid={`${testIdPrefix}-unsupported`}>
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-400" />
              <p>Este tipo de archivo no puede previsualizarse en el navegador.</p>
              <p className="text-xs text-slate-500 mt-1">Usa el botón <b>Descargar</b> para verlo.</p>
              <p className="text-[11px] text-slate-400 mt-2">Tipo detectado: {blobType || "desconocido"}</p>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t bg-white">
          <Button
            variant="outline"
            onClick={downloadFromPreview}
            disabled={!blobUrl}
            data-testid={`${testIdPrefix}-download`}
          >
            <Download className="w-4 h-4 mr-1" /> Descargar
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid={`${testIdPrefix}-close`}
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPreviewDialog;
